import React, { useCallback, useEffect, useState } from "react";
import { Flex, Dialog, CloseIcon, Button, Loader } from "@fluentui/react-northstar";
import { RelationTypeSelection } from "./DialogPages/RelationTypeSelection";
import { TicketsSelection } from "./DialogPages/TicketsSelection";
import { RelateTicketsConfirmation } from "./DialogPages/RelateTicketsConfirmation";
import { IRelateTickets, RelationType } from "./RelateTicketHelper";
import { appState } from '../../../AppState';
import { useTranslation } from 'react-i18next';
import { platformService } from "../../services/platform.service";
import { LIFECYCLE_STATUS_GUID, TICKET_ITEM_PER_PAGE } from '../../utils/constants';

interface Props {
  oldRelatedTicketIds: number[];
  sourceTicketId: number;
  open: boolean;
  onCancel: () => void;
  relateTickets: (relateTicketData: IRelateTickets) => void;
}

export interface RelateTicket {
  sourceTicketId: number,
  relatedTicketIds: number[],
  relationShipType: number,
}

const initalRelatedTicketData: RelateTicket = {
  sourceTicketId: 0,
  relatedTicketIds: [],
  relationShipType: 0,
}
const defaultItemsPerPage = parseInt(TICKET_ITEM_PER_PAGE.find(item => item.value === '50')?.value || TICKET_ITEM_PER_PAGE[0]?.value || '50', 10) || 50;

export const RelateTicketDialog = ({ oldRelatedTicketIds, sourceTicketId, open, onCancel, relateTickets }: Props) => {
  const {t} = useTranslation();
  const currentState = appState();
  const [menu, setMenu] = useState<number>(0);
  const [relatedTicketData, setRelatedTicketData] = useState<RelateTicket>(initalRelatedTicketData);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(defaultItemsPerPage);
  const [searchKey, setSearchKey] = useState<string>('');
  const [statusId, setStatusId] = useState<number>(0);
  const [isTicketsLoading, setIsTicketsLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const RELATION_SELECTION = "relation-selection";
  const TICKETS_SELECTION = "tickets-selection";
  const CONFIRMATION = "confirmation";
  const pages = [RELATION_SELECTION, TICKETS_SELECTION, CONFIRMATION];
  const api = new platformService();

  useEffect(() => {
    if (open) {
      setIsLoaded(true);
      setCurrentPage(1);
      setTotalCount(0);
      setSearchKey('');
      setStatusId(0);
      setFilteredTickets([]);
      setRelatedTicketData({ ...initalRelatedTicketData, sourceTicketId });
    }
  }, [open, sourceTicketId]);

  const buildFilterQuery = (relationShipType: number, selectedStatusId: number, selectedSearchKey: string) => {
    const filters: string[] = [`Id ne ${sourceTicketId}`];
    if (relationShipType === RelationType.MergeTickets) {
      filters.push("Closed eq false");
    }
    if (relationShipType === RelationType.RelateTickets && selectedStatusId > 0) {
      filters.push(`StatusId eq ${selectedStatusId}`);
    }

    const normalizedSearch = selectedSearchKey?.trim().toLowerCase();
    if (normalizedSearch) {
      const escapedSearch = normalizedSearch.replace(/'/g, "''");
      const numericSearch = parseInt(normalizedSearch, 10);
      const searchFilters = [`contains(tolower(Title),'${escapedSearch}')`];
      if (!isNaN(numericSearch)) {
        searchFilters.push(`Id eq ${numericSearch}`);
      }
      filters.push(`(${searchFilters.join(' or ')})`);
    }

    return filters.length > 0 ? `$filter=${filters.join(' and ')}&` : '';
  }

  const loadTicketsPage = useCallback(async (relationShipType: number, page: number, pageSize: number, selectedStatusId: number = 0, selectedSearchKey: string = '') => {
    setIsTicketsLoading(true);
    try {
      const filterQuery = buildFilterQuery(relationShipType, selectedStatusId, selectedSearchKey);
      const expandQuery = relationShipType === RelationType.MergeTickets ? '$expand=TicketLifecycle($expand=Status)&' : '';
      const query = `?${filterQuery}${expandQuery}$orderby=CreatedDate desc&$top=${pageSize}&$skip=${(page - 1) * pageSize}&$count=true`;
      const { data } = await api.getTicketList(query);
      const ticketPage = data?.value || [];
      setTotalCount(data?.['@odata.count'] ?? 0);
      applyRelationFilter(ticketPage, relationShipType);
      setCurrentPage(page);
    } finally {
      setIsTicketsLoading(false);
    }
  }, [sourceTicketId]);

  const applyRelationFilter = (tickets: Ticket[], relationShipType: number) => {
    let filteredTicketsList: Ticket[] = [...tickets];
    switch (relationShipType) {
      case RelationType.MergeTickets: {
        filteredTicketsList.forEach(item => item.ticketApprovals = currentState.ticketApprovals?.find(x => x.TicketId == item.Id) || null);
        filteredTicketsList = filteredTicketsList.filter((ticket: Ticket) => {
          const lifecycleIsActive = (ticket.TicketLifecycle?.Status ?? false) &&
            [LIFECYCLE_STATUS_GUID.NOT_STARTED, LIFECYCLE_STATUS_GUID.IN_PROGRESS].indexOf(ticket.TicketLifecycle.Status.Guid) >= 0;
          return !ticket.Closed && !lifecycleIsActive && (ticket.ticketApprovals?.length ?? 0) === 0 && (ticket.TicketTasks?.length ?? 0) === 0;
        });
        break;
      }
      case RelationType.RelateTickets: {
        const checkedTickets = filteredTicketsList.filter(t => oldRelatedTicketIds.includes(t.Id));
        const unCheckedTickets = filteredTicketsList.filter(t => !oldRelatedTicketIds.includes(t.Id));
        filteredTicketsList = [...checkedTickets, ...unCheckedTickets];
        break;
      }
      default: break;
    }
    setFilteredTickets(filteredTicketsList);
  }

  const closeDialog = () => {
    setMenu(pages.indexOf(RELATION_SELECTION));
    onCancel();
    setRelatedTicketData(initalRelatedTicketData);
  }

  const onRelationTypeSelection = useCallback((relationShipType: number) => {
    loadTicketsPage(relationShipType, 1, itemsPerPage, 0, '');
    let relatedTicketIds = relatedTicketData.relatedTicketIds;
    if (relationShipType === RelationType.MergeTickets) relatedTicketIds = [];
    if (relationShipType === RelationType.RelateTickets) relatedTicketIds = oldRelatedTicketIds;
    setSearchKey('');
    setStatusId(0);
    setRelatedTicketData({ ...relatedTicketData, relationShipType, relatedTicketIds });
    setMenu(pages.indexOf(TICKETS_SELECTION));
  }, [relatedTicketData, itemsPerPage, oldRelatedTicketIds, loadTicketsPage]);

  const onPageChange = useCallback((page: number, pageSize: number) => {
    if (!relatedTicketData.relationShipType) return;
    loadTicketsPage(relatedTicketData.relationShipType, page, pageSize, statusId, searchKey);
  }, [relatedTicketData, loadTicketsPage, statusId, searchKey]);

  const onFiltersChange = useCallback((selectedSearchKey: string, selectedStatusId: number) => {
    setSearchKey(selectedSearchKey);
    setStatusId(selectedStatusId);
    if (!relatedTicketData.relationShipType) return;
    loadTicketsPage(relatedTicketData.relationShipType, 1, itemsPerPage, selectedStatusId, selectedSearchKey);
  }, [relatedTicketData, loadTicketsPage, itemsPerPage]);

  const primaryClick = useCallback(async () => {
    let nextIndex = menu + 1;
    if (nextIndex < pages.length) {
      setMenu(nextIndex);
    }
    else {
      saveData();
      closeDialog();
    }
  }, [menu, relatedTicketData]);

  const secondaryClick = useCallback(() => {
    let nextIndex = menu - 1;
    if (nextIndex >= 0) setMenu(nextIndex);
    else closeDialog();
  }, [open, menu, relatedTicketData]);

  const saveData = async () => {
    relateTickets({
      SourceTicketId: relatedTicketData.sourceTicketId,
      RelatedTicketIds: relatedTicketData.relatedTicketIds,
      RelationshipType: relatedTicketData.relationShipType
    });
  }

  const isNoChangeInSelection = () => {
    return menu === pages.indexOf(TICKETS_SELECTION) &&
    ((relatedTicketData.relationShipType === RelationType.MergeTickets && relatedTicketData.relatedTicketIds.length === 0) ||
    (relatedTicketData.relationShipType === RelationType.RelateTickets && getIdsString(oldRelatedTicketIds) === getIdsString(relatedTicketData.relatedTicketIds)));
  }

  const getIdsString = (ticketIds: number[]) => [...ticketIds].sort((a, b) => (a - b)).join('~');

  const renderPages = () => {
    switch (pages[menu]) {
      case RELATION_SELECTION:
        return <RelationTypeSelection onRelationTypeSelection={onRelationTypeSelection} />
      case TICKETS_SELECTION:
        return <TicketsSelection
          relatedTicketData={relatedTicketData}
          setRelatedTicketData={setRelatedTicketData}
          tickets={filteredTickets}
          totalCount={totalCount}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          isLoading={isTicketsLoading}
          onPageChange={onPageChange}
          onItemsPerPageChange={setItemsPerPage}
          onFiltersChange={onFiltersChange}
        />
      case CONFIRMATION:
        return <RelateTicketsConfirmation relatedTicketData={relatedTicketData} oldRelatedTicketIds={oldRelatedTicketIds} />
      default:
        return (<></>)
    }
  }

  return (
    <Dialog className="relate-ticket-dialog"
      content={
        <>
          {isLoaded && renderPages()}
          {isLoaded && relatedTicketData.relationShipType === RelationType.MergeTickets && menu === 1 &&
              <Flex.Item>
                <div style={{ marginLeft: 'unset', marginTop: '5px' }}>
                  <span className='font-bold'>{t('ticket-details.edit-ticket-form.relate-ticket.dialog.footer.note')}: </span>
                  <span>{t('ticket-details.edit-ticket-form.relate-ticket.dialog.footer.ticket-approval')}</span>
                </div>
              </Flex.Item>
            }
          {!isLoaded && <div className={`pt-5 mt-5`}><Loader /></div>}
        </>
      }
      open={open}
      headerAction={{
        icon: <CloseIcon />,
        title: t('ticket-details.edit-ticket-form.relate-ticket.dialog.button.close'),
        onClick: closeDialog
      }}
      footer={
        <Flex gap="gap.small">
          {menu > 0 && <>
            <Flex.Item push>
              <Button content={t('ticket-details.edit-ticket-form.relate-ticket.dialog.button.back')} onClick={secondaryClick} />
            </Flex.Item>
            <Button className="dialog-btn"
              disabled={isNoChangeInSelection()}
              content={menu + 1 < pages.length ? t('ticket-details.edit-ticket-form.relate-ticket.dialog.button.continue') : t('ticket-details.edit-ticket-form.relate-ticket.dialog.button.confirm')}
              primary onClick={primaryClick}
            />
          </>}
        </Flex>
      }
    />
  )
}